import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { BPSAdminLevel, PermissionsEnum, PermissionsService } from 'shared-lib';
import { ProjectDesignation, ProjectPaymentStatus, ProjectType } from 'src/app/models/projectDesignation.model';
import { ProjectDesignationService } from 'src/app/services/project-designation.service';
import { TranslationUtils } from 'src/assets/i18n/translation.utils';

@Component({
  selector: 'crud-project-designation',
  templateUrl:
    './crud-project-designation.component.html',
  styleUrls: ['./crud-project-designation.component.scss']
})
export class CrudProjectDesignationComponent implements OnInit {

  @Input() searchText: string;
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;
  @ViewChild('closeDeleteModal') closeDeleteModal: ElementRef;
  @ViewChild('closeAddEditModal') closeAddEditModal: ElementRef;

  public PremissionsEnum = PermissionsEnum;

  public currentPage = 1;
  public pageSize = 5;
  public count: number = 0;

  projectDesignations: ProjectDesignation[] = [];
  selectedProjectDesignation: ProjectDesignation = new ProjectDesignation();

  projectTypes = [
    { label: 'KCG', value: ProjectType.KCG },
    { label: 'MAP', value: ProjectType.MAP },
    { label: 'NMAP', value: ProjectType.NMAP },
  ];
  selectedProjectType: ProjectType | undefined;

  projectPaymentStatuses = [
    { label: 'Paid', value: ProjectPaymentStatus.Paid },
    { label: 'Non-Paid', value: ProjectPaymentStatus.NonPaid },
  ];
  selectedProjectPaymentStatus: ProjectPaymentStatus | undefined;

  isEditMode: boolean = false;

  actionLabel$!: Observable<string>;
  messages$: Observable<{ [key: string]: string }>;

  constructor(
    private toastr: ToastrService,
    private translateService: TranslateService,
    public permissionsService: PermissionsService,
    private projectDesignationService: ProjectDesignationService
  ) { }

  ngOnInit(): void {
    this.reloadTable();
  }

  reloadTable(searchText?: string, page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: searchText ?? "",
      },
    };
    this.getAllProjectDesignations(dataIn);
  }

  private getAllProjectDesignations(dataIn: any) {
    this.projectDesignationService.getAll(dataIn).subscribe({
      next: (response) => {
        if(response?.status === 200 || response?.status === 'OK') {
          this.projectDesignations = response.data.data;
          this.count = response.data.count;
        }else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
           this.toastr.error(
            messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]
          );
        })
      }
    })
  }

  filter() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: this.searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllProjectDesignations(dataIn);
  }

  private updateActionLabel(): void {
    const key = this.isEditMode
      ? TranslationUtils.EDIT_KEY
      : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  addNew() {
    this.isEditMode = false;
    this.selectedProjectDesignation = new ProjectDesignation();
    this.selectedProjectType = undefined;
    this.selectedProjectPaymentStatus = undefined;
    this.updateActionLabel();
  }

  selectProjectDesignation(projectDesignation: ProjectDesignation) {
    this.selectedProjectDesignation = { ...projectDesignation };
    this.selectedProjectType = ProjectType[
      this.selectedProjectDesignation.projectType?.toString() as keyof typeof ProjectType
    ];
    this.selectedProjectPaymentStatus = ProjectPaymentStatus[
      this.selectedProjectDesignation.projectPaymentStatus?.toString() as keyof typeof ProjectPaymentStatus
    ];
    this.updateActionLabel();
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable(this.searchText);
  }

  onModalFade() {
    this.selectedProjectDesignation = new ProjectDesignation();
    this.selectedProjectType = undefined;
    this.selectedProjectPaymentStatus = undefined;
  }

  isValid(): boolean {
    return this.selectedProjectDesignation.name !== undefined &&
            this.selectedProjectDesignation.name.trim().length !== 0 &&
          this.selectedProjectType !== undefined && 
          this.selectedProjectPaymentStatus !== undefined;
  }
  save() {
    if(!this.isValid()) {
      this.toastr.warning("Please fill all the fields");
      return;
    }
    var dataIn = new ProjectDesignation({
      id: this.selectedProjectDesignation.id,
      name: this.selectedProjectDesignation.name,
      projectType: this.selectedProjectType,
      projectPaymentStatus: this.selectedProjectPaymentStatus
    });
    this.projectDesignationService.save(dataIn).subscribe({
      next: (response) => {
        if(response?.status === 200 || response?.status === 'OK') {
          this.closeAddEditModal?.nativeElement?.click();
            this.toastr.success(response.message);
            if (this.selectedProjectDesignation.id === null) {
              this.pageChange(1);
              return;
            }
            this.reloadTable(this.searchText);
        }else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
           this.toastr.error(
            messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]
          );
        })
      } ,
    })

  }

  deleteProjectDesignation() {
    this.projectDesignationService.delete(this.selectedProjectDesignation.id!).subscribe({
      next: (response) => {
        this.closeDeleteModal?.nativeElement?.click();
        if(response.status === 200 || response.status === "OK") {
          this.pageChange(1);
          this.toastr.success(response.message);
        }else{
          this.toastr.error(response.message);
        }
      },
      error: (_) => {
        this.messages$.subscribe((messages) => {
          this.toastr.error(messages[TranslationUtils.WARNING_ERROR_OCCURRED_KEY]);
        })
      }
    })
  }

}
